import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Paper,
    Typography,
    Snackbar,
    Alert,
    Link
} from '@mui/material';
import { GeneralInformation } from './steps/GeneralInformation';
import { CreditAccountDetails } from './steps/CreditAccountDetails';
import { Confirmation } from './steps/Confirmation';
import { useNavigate, useLocation } from 'react-router';
import Error from '../../../errors/Error';
import ETLogo from '../../../assets/environment-testing.png';
import axios from 'axios';
import { getClientOnboardingStates } from 'services';
import { getErrorMessage } from 'helpers/apiErrorEvent';

const getTokenPayload = (token: string | null) => {
    if (!token) {
        return null;
    }

    try {
        return JSON.parse(atob(token.split('.')[1] || ''));
    } catch {
        return null;
    }
};

interface FormData {
    declaration: {
        hasAgreedToTermsAndCondition: boolean;
        title: string;
        fullName: string;
        company: string;
        tandCComments: string;
        requestedDate: string;
        signatureId?: string;
    };
    companyName: string;
    abn: string;
    emailAddress: string;
    contactName: string;
    contactNumberType?: 'phoneNumber' | 'mobileNumber' | '';
    mobileNumber: string;
    eurofinsContactName: string;
    tradingName: string;
    phoneNumber: string;
    id?: string;
    businessAddress: {
        street: string;
        suburb: string;
        state: string;
        postcode: string;
        country: string;
    };
    postalAddress: {
        street: string;
        suburb: string;
        state: string;
        postcode: string;
        country: string;
    };
    isPostalAddressSameAsBusinessAddress: boolean;
    signatureId?: string;
    invoiceStatement: {
        contactName: string;
        phoneNumber: string;
        emailAddressStatements: string;
        emailAddressInvoices: string;
        invoicePlatform: string;
        isPONumberMandatory: string;
        hasInvoicePlatform: string;
        needCreditAccount: string;
        poEmail: string;
    };

    creditAccountData: any;
    requestedContacts: any[];
    requestStatus: any;
    proposeClientCode?: string;
    proposedPriceListID?: string;
    proposedELECodes?: string;
    proposedReportStateID?: string;
    proposedIsGSTExempt?: boolean;
    proposedBUCode?: string;
    proposedPaymentTermsID?: string;
    proposedIsMonthlyInvoice?: boolean;
    proposedASM?: string;
    proposedIsPORequired?: boolean;
    abnCheck?: string;
    refereeCheck?: string;
    reviewerComments?: string;
    bankLetterFileName?: string;
}

interface SnackbarState {
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
}

const formatABN = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 11);

    return digits
        .replace(/(\d{2})(\d{3})(\d{3})(\d{3})/, '$1 $2 $3 $4')
        .trim();
};

export const NewAccountRegistration = () => {
    const [activeStep, setActiveStep] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isDisabled, setIsDisabled] = useState(false);
    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
    const [snackbar, setSnackbar] = useState<SnackbarState>({
        open: false,
        message: '',
        severity: 'info'
    });
    const [id, setId] = useState<string>('');
    const [captureSignature, setCaptureSignature] = useState<File | null>(null);
    const [australianStates, setAustralianStates] = useState<any[]>([]);
    const navigate = useNavigate();
    const location = useLocation();
    const accessTokenValue = localStorage.getItem('token');
    const tokenPayload: any = getTokenPayload(accessTokenValue);
    const loggedInEmail = location.state?.email || tokenPayload?.email || '';
    const validUser = tokenPayload?.ValidOTP === 'True' && tokenPayload?.OnboardRequest === 'True';

    const [formData, setFormData] = useState<FormData>({
        companyName: '',
        abn: '',
        emailAddress: loggedInEmail,
        contactName: '',
        contactNumberType: '',
        mobileNumber: '',
        eurofinsContactName: '',
        tradingName: '',
        phoneNumber: '',
        businessAddress: {
            street: '',
            suburb: '',
            state: '',
            postcode: '',
            country: 'Australia'
        },
        postalAddress: {
            street: '',
            suburb: '',
            state: '',
            postcode: '',
            country: 'Australia'
        },
        signatureId: '',
        isPostalAddressSameAsBusinessAddress: true,
        invoiceStatement: {
            contactName: '',
            phoneNumber: '',
            emailAddressStatements: '',
            emailAddressInvoices: '',
            invoicePlatform: '',
            isPONumberMandatory: '',
            hasInvoicePlatform: '',
            needCreditAccount: 'no',
            poEmail: ''
        },
        creditAccountData: {
            contactName: '',
            email: '',
            position: '',
            phoneNumber: '',
            companyWebsite: '',
            natureOfBusiness: '',
            dateOfIncorporation: '',
            parentCompany: '',
            previousBusiness: '',
            hasCreditReferees: '',
            creditReferees: [
                {
                    contactName: '',
                    company: '',
                    phoneNumber: '',
                    email: ''
                }
            ],
            maxCreditRequired: 0,
            isTrustee: '',
            directorName: ''
        },
        requestedContacts: [],
        declaration: {
            hasAgreedToTermsAndCondition: false,
            title: '',
            fullName: '',
            company: '',
            tandCComments: '',
            requestedDate: ''
        },
        requestStatus: ''
    });

    useEffect(() => {
        if (!validUser) {
            return;
        }

        getClientOnboardingStates().then((res: any) => {
            if (res.status === 200) {
                setAustralianStates(res?.data || []);
            }
        });
    }, [validUser]);

    const getAustralianStateId = (value: any) => {
        if (value === null || value === undefined || value === '') {
            return '';
        }

        const matchedState = australianStates.find((state: any) => (
            String(state?.id) === String(value)
            || state?.name === value
            || state?.displayName === value
            || state?.value === value
        ));

        return matchedState ? String(matchedState.id) : String(value);
    };

    const normalizeAustralianAddressState = (country: string, stateValue: any) => {
        if (country !== 'Australia') {
            return stateValue || '';
        }

        return getAustralianStateId(stateValue);
    };

    useEffect(() => {
        if (!australianStates.length) {
            return;
        }

        setFormData((prev) => {
            const normalizedBusinessState = normalizeAustralianAddressState(prev.businessAddress?.country, prev.businessAddress?.state);
            const normalizedPostalState = normalizeAustralianAddressState(prev.postalAddress?.country, prev.postalAddress?.state);

            if (
                normalizedBusinessState === (prev.businessAddress?.state || '')
                && normalizedPostalState === (prev.postalAddress?.state || '')
            ) {
                return prev;
            }

            return {
                ...prev,
                businessAddress: {
                    ...prev.businessAddress,
                    state: normalizedBusinessState
                },
                postalAddress: {
                    ...prev.postalAddress,
                    state: normalizedPostalState
                }
            };
        });
    }, [australianStates]);

    const normalizeMaxCreditRequired = (value: any) => {
        if (value === '' || value === null || value === undefined) {
            return 0;
        }

        return value;
    };

    const mapApiValidationErrorKey = (key: string) => {
        const normalizedKey = key.toLowerCase();

        const requestedContactFieldMap: Record<string, string> = {
            'requestedcontacts[0].name': 'contactName',
            'requestedcontacts[0].email': 'emailAddress',
            'requestedcontacts[0].emailforstatements': 'invoiceStatement.emailAddressStatements',
            'requestedcontacts[0].phonenumber': 'contactNumber',
            'requestedcontacts[0].mobilenumber': 'contactNumber',
            'requestedcontacts[1].name': 'invoiceStatement.contactName',
            'requestedcontacts[1].email': 'invoiceStatement.emailAddressInvoices',
            'requestedcontacts[1].emailforstatements': 'invoiceStatement.emailAddressStatements',
            'requestedcontacts[1].phonenumber': 'invoiceStatement.phoneNumber'
        };

        if (requestedContactFieldMap[normalizedKey]) {
            return requestedContactFieldMap[normalizedKey];
        }

        const clientOnboardFieldMap: Record<string, string> = {
            'companyname': 'companyName',
            'tradingname': 'tradingName',
            'abn': 'abn',
            'eurofinscontactname': 'eurofinsContactName',
            'business_streetname': 'businessAddress.street',
            'business_suburb': 'businessAddress.suburb',
            'business_state': 'businessAddress.state',
            'business_postcode': 'businessAddress.postcode',
            'business_country': 'businessAddress.country',
            'postal_streetname': 'postalAddress.street',
            'postal_suburb': 'postalAddress.suburb',
            'postal_state': 'postalAddress.state',
            'postal_postcode': 'postalAddress.postcode',
            'postal_country': 'postalAddress.country',
            'poemail': 'invoiceStatement.poEmail',
            'invoiceplatform': 'invoiceStatement.invoicePlatform'
        };

        if (normalizedKey.startsWith('clientonboardrequest.')) {
            const fieldName = normalizedKey.replace('clientonboardrequest.', '');
            return clientOnboardFieldMap[fieldName] || key;
        }

        const creditAccountFieldMap: Record<string, string> = {
            'contactname': 'contactName',
            'email': 'email',
            'position': 'position',
            'phonenumber': 'phoneNumber',
            'companywebsite': 'companyWebsite',
            'natureofbusiness': 'natureOfBusiness',
            'dateofincorporation': 'dateOfIncorporation',
            'parentcompany': 'parentCompany',
            'previousbusiness': 'previousBusiness',
            'maxcreditrequired': 'maxCreditRequired',
            'istrustee': 'isTrustee',
            'directorname': 'directorName',
            'hascreditreferees': 'hasCreditReferees'
        };

        if (normalizedKey.startsWith('creditaccountdetail.')) {
            const fieldName = normalizedKey.replace('creditaccountdetail.', '');
            return creditAccountFieldMap[fieldName] || key;
        }

        return key;
    };

    const getValidationErrorStep = (errors: Record<string, any>) => {
        if (skipCreditAccountStep) {
            return 0;
        }

        const hasCreditErrors = Object.keys(errors).some((key) => {
            const normalizedKey = key.toLowerCase();
            return normalizedKey.startsWith('creditaccountdetail.') || normalizedKey.startsWith('creditreferees');
        });

        return hasCreditErrors ? 1 : 0;
    };

    const handleApiValidationError = (responseData: any) => {
        if (!responseData?.errors || typeof responseData.errors !== 'object') {
            return false;
        }

        const mappedErrors = Object.entries(responseData.errors).reduce((acc, [key, value]) => {
            const message = Array.isArray(value) ? value[0] : value;
            acc[mapApiValidationErrorKey(key)] = String(message);
            return acc;
        }, {} as Record<string, string>);

        const firstErrorMessage = Object.values(mappedErrors)[0] || responseData?.title || 'One or more validation errors occurred.';

        setValidationErrors(mappedErrors);
        setActiveStep(getValidationErrorStep(responseData.errors));
        showSnackbar(firstErrorMessage, 'error');

        return true;
    };

    const paramMapping = (latestRequest: any) => {
        // Helper function for date formatting
        const formatDate = (dateString: string) => {
            if (!dateString) return '';
            const d = new Date(dateString);
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const dd = String(d.getDate()).padStart(2, '0');
            const yyyy = d.getFullYear();
            return `${yyyy}-${mm}-${dd}`;
        };

        const mappedFormData: Partial<FormData> = {
            id: latestRequest.clientOnboardRequest.id || '',
            companyName: latestRequest.clientOnboardRequest.companyName || '',
            abn: latestRequest.clientOnboardRequest.abn || '',
            emailAddress: latestRequest.requestedContacts?.find((c: any) => c.isDefault && !c.isInvoice)?.email || '',
            contactName: latestRequest.requestedContacts?.find((c: any) => c.isDefault && !c.isInvoice)?.name || '',
            contactNumberType: latestRequest.requestedContacts?.find((c: any) => c.isDefault && !c.isInvoice)?.phoneNumber
                ? 'phoneNumber'
                : (latestRequest.requestedContacts?.find((c: any) => c.isDefault && !c.isInvoice)?.mobileNumber ? 'mobileNumber' : ''),
            mobileNumber: latestRequest.requestedContacts?.find((c: any) => c.isDefault && !c.isInvoice)?.mobileNumber || '',
            eurofinsContactName: latestRequest.clientOnboardRequest.eurofinsContactName || '',
            tradingName: latestRequest.clientOnboardRequest.tradingName || '',
            phoneNumber: latestRequest.requestedContacts?.find((c: any) => c.isDefault && !c.isInvoice)?.phoneNumber || '',
            businessAddress: {
                street: latestRequest.clientOnboardRequest.business_StreetName || '',
                suburb: latestRequest.clientOnboardRequest.business_Suburb || '',
                state: normalizeAustralianAddressState(
                    latestRequest.clientOnboardRequest.business_Country || 'Australia',
                    latestRequest.clientOnboardRequest.business_State
                ),
                postcode: latestRequest.clientOnboardRequest.business_PostCode || '',
                country: latestRequest.clientOnboardRequest.business_Country || 'Australia'
            },
            postalAddress: {
                street: latestRequest.clientOnboardRequest.postal_StreetName || '',
                suburb: latestRequest.clientOnboardRequest.postal_Suburb || '',
                state: normalizeAustralianAddressState(
                    latestRequest.clientOnboardRequest.postal_Country || 'Australia',
                    latestRequest.clientOnboardRequest.postal_State
                ),
                postcode: latestRequest.clientOnboardRequest.postal_PostCode || '',
                country: latestRequest.clientOnboardRequest.postal_Country || 'Australia'
            },
            // signatureId: latestRequest.clientOnboardRequest.signatureId || '',
            isPostalAddressSameAsBusinessAddress: latestRequest.clientOnboardRequest.isPostalAddressSameAsBusinessAddress || false,
            invoiceStatement: {
                contactName: latestRequest.requestedContacts?.find((c: any) => !c.isDefault && c.isInvoice)?.name || '',
                phoneNumber: latestRequest.requestedContacts?.find((c: any) => !c.isDefault && c.isInvoice)?.phoneNumber || '',
                emailAddressStatements: latestRequest.requestedContacts?.find((c: any) => !c.isDefault && c.isInvoice)?.emailForStatements || '',
                emailAddressInvoices: latestRequest.requestedContacts?.find((c: any) => !c.isDefault && c.isInvoice)?.email || '',
                invoicePlatform: latestRequest.clientOnboardRequest.invoicePlatform || '',
                isPONumberMandatory: latestRequest.clientOnboardRequest.isPONumberMandatory ? 'yes' : 'no',
                hasInvoicePlatform: latestRequest.clientOnboardRequest.hasInvoicePlatform ? 'yes' : 'no',
                needCreditAccount: latestRequest.clientOnboardRequest.needCreditAccount ? 'yes' : 'no',
                poEmail: latestRequest.clientOnboardRequest.poEmail || ''
            },
            creditAccountData: {
                ...formData.creditAccountData,
                id: latestRequest.creditAccountDetail?.id || '',
                clientOnBoardRequestID: latestRequest.creditAccountDetail?.clientOnBoardRequestID || '',
                contactName: latestRequest.creditAccountDetail?.contactName || '',
                email: latestRequest.creditAccountDetail?.email || '',
                position: latestRequest.creditAccountDetail?.position || '',
                phoneNumber: latestRequest.creditAccountDetail?.phoneNumber || '',
                companyWebsite: latestRequest.creditAccountDetail?.companyWebsite || '',
                natureOfBusiness: latestRequest.creditAccountDetail?.natureOfBusiness || '',
                dateOfIncorporation: formatDate(latestRequest.creditAccountDetail?.dateOfIncorporation), // FIXED: Now uses correct source
                parentCompany: latestRequest.creditAccountDetail?.parentCompany || '',
                previousBusiness: latestRequest.creditAccountDetail?.previousBusiness || '',
                hasCreditReferees: latestRequest.creditAccountDetail?.hasCreditReferees ? 'yes' : 'no',
                maxCreditRequired: latestRequest.creditAccountDetail?.maxCreditRequired ?? 0,
                isTrustee: latestRequest.creditAccountDetail?.isTrustee ? 'yes' : 'no',
                directorName: latestRequest.creditAccountDetail?.directorName || '',
                creditReferees: Array.isArray(latestRequest.creditReferees)
                    ? latestRequest.creditReferees.map((ref: any) => ({
                        id: ref.id || '',
                        contactName: ref.contactName || '',
                        email: ref.email || '',
                        phoneNumber: ref.phoneNumber || '',
                        company: ref.company || '',
                        creditAccountID: ref.creditAccountID || ''
                    }))
                    : []
            },
            requestedContacts: latestRequest.requestedContacts || [],
            declaration: {
                hasAgreedToTermsAndCondition: latestRequest.clientOnboardRequest.hasAgreedToTermsAndCondition || false,
                title: latestRequest.clientOnboardRequest.title || '',
                fullName: latestRequest.clientOnboardRequest.fullName || '',
                company: latestRequest.clientOnboardRequest.company || '',
                // signatureId: latestRequest.clientOnboardRequest.signatureId || '',
                tandCComments: latestRequest.clientOnboardRequest.tandCComments || '',
                requestedDate: formatDate(latestRequest.clientOnboardRequest.requestedDate) // Using helper function
            },
            requestStatus: latestRequest.clientOnboardRequest.requestStatus,
            proposeClientCode: latestRequest.clientOnboardRequest.proposeClientCode,
            proposedPriceListID: latestRequest.clientOnboardRequest.proposedPriceListID,
            proposedELECodes: latestRequest.clientOnboardRequest.proposedELECodes,
            proposedReportStateID: latestRequest.clientOnboardRequest.proposedReportStateID,
            proposedIsGSTExempt: latestRequest.clientOnboardRequest.proposedIsGSTExempt,
            proposedBUCode: latestRequest.clientOnboardRequest.proposedBUCode,
            proposedPaymentTermsID: latestRequest.clientOnboardRequest.proposedPaymentTermsID,
            proposedIsMonthlyInvoice: latestRequest.clientOnboardRequest.proposedIsMonthlyInvoice,
            proposedASM: latestRequest.clientOnboardRequest.proposedASM,
            proposedIsPORequired: latestRequest.clientOnboardRequest.proposedIsPORequired,
            abnCheck: latestRequest.clientOnboardRequest.abnCheck,
            refereeCheck: latestRequest.clientOnboardRequest.refereeCheck,
            reviewerComments: latestRequest.clientOnboardRequest.reviewerComments || '',
            bankLetterFileName: latestRequest.clientOnboardRequest.bankLetterFileName || ''
        };
        setFormData(prev => ({ ...prev, ...mappedFormData }));
        setId(latestRequest.clientOnboardRequest.id || '');
    }

    const getOnBoardClientDetails = async () => {
        await axios.get(`${process.env.REACT_APP_BASE_URL}/OnboardClientRequest/GetOnboardRequestsForClient`,
            {
                responseType: 'json',
                headers: {
                    'Authorization': accessTokenValue ? `Bearer ${accessTokenValue}` : '',
                    "content-type": "application/json"
                }
            }
        ).then((res: any) => {
            if (res?.status === 200) {
                if (res.data == -1) {
                    setIsDisabled(true);
                }
                else if (res?.data) {
                    const latestRequest = res.data;
                    paramMapping(latestRequest);
                }
            }
        }).catch((error: any) => {
            if (error?.response?.status === 404) {
                setSnackbar({
                    open: true,
                    message: getErrorMessage(error, 'No existing onboard requests found for this email.'),
                    severity: 'warning'
                });
            } else {
                setSnackbar({
                    open: true,
                    message: getErrorMessage(error, 'No existing onboard requests found for this email.'),
                    severity: 'error'
                });
            }
        });
    }

    useEffect(() => {
        if (loggedInEmail?.length > 0 && accessTokenValue?.length > 0 && validUser) {
            getOnBoardClientDetails();
        }
    }, [loggedInEmail, accessTokenValue, validUser]);

    if (!validUser) {
        return <Error />;
    }

    const skipCreditAccountStep = formData.invoiceStatement?.needCreditAccount === 'no';

    const getSteps = () => {
        if (skipCreditAccountStep) {
            return [
                'General Information',
                'Review & Confirmation'
            ];
        }
        return [
            'General Information',
            'Credit Account Details',
            'Review & Confirmation'
        ];
    };

    const steps = getSteps();

    const handleNext = () => {
        setValidationErrors({});
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = () => {
        setValidationErrors({});
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleSkipToReview = () => {
        setValidationErrors({});
        setActiveStep(1);
    };

    const handleBackToGeneralInfo = () => {
        setValidationErrors({});
        setActiveStep(0);
    };

    const handleFormDataChange = (newData: Partial<FormData>) => {
        setFormData(prev => ({ ...prev, ...newData }));
    };

    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
        setSnackbar({
            open: true,
            message,
            severity
        });
    };

    const buildSavePayload = (declarationData?: any) => {
        const now = new Date();
        const day = String(now.getDate()).padStart(2, '0');
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const year = now.getFullYear();
        const currentDate = `${year}-${month}-${day}`;

        const requestedContacts = [];

        const generalContact = {
            "name": formData.contactName,
            "email": formData.emailAddress,
            "emailForStatements": "",
            "phoneNumber": formData.phoneNumber,
            "mobileNumber": formData.mobileNumber,
            "isDefault": true,
            "isInvoice": false,
            "requestedOn": currentDate,
            "requestedByEmail": loggedInEmail,
            "modifiedByEmail": loggedInEmail
        };
        requestedContacts.push(generalContact);

        if (formData.invoiceStatement.contactName) {
            const invoiceContact = {
                "name": formData.invoiceStatement.contactName,
                "email": formData.invoiceStatement.emailAddressInvoices,
                "emailForStatements": formData.invoiceStatement.emailAddressStatements,
                "phoneNumber": formData.invoiceStatement.phoneNumber,
                "mobileNumber": "",
                "isDefault": false,
                "isInvoice": true,
                "requestedOn": currentDate,
                "requestedByEmail": loggedInEmail,
                "modifiedByEmail": loggedInEmail
            };
            requestedContacts.push(invoiceContact);
        }

        let isDraftOnboarding = formData.requestStatus !== 5 ? "draftClientOnBoardRequestID" : "clientOnboardRequestID";
        let isCreditAccountDraft = formData.requestStatus !== 5 ? "draftCreditAccountID" : "creditAccountID";

        const clientOnboardRequest: any = {
            "companyName": formData.companyName,
            "tradingName": formData.tradingName,
            "abn": formatABN(formData.abn),
            "eurofinsContactName": formData.eurofinsContactName,
            "business_StreetName": formData.businessAddress.street,
            "business_Suburb": formData.businessAddress.suburb,
            "business_State": formData.businessAddress.state,
            "business_Country": formData.businessAddress.country,
            "business_PostCode": formData.businessAddress.postcode,
            "isPostalAddressSameAsBusinessAddress": formData.isPostalAddressSameAsBusinessAddress,
            "postal_StreetName": formData.postalAddress.street,
            "postal_Suburb": formData.postalAddress.suburb,
            "postal_State": formData.postalAddress.state,
            "postal_Country": formData.postalAddress.country,
            "postal_PostCode": formData.postalAddress.postcode,
            "isPONumberMandatory": formData.invoiceStatement.isPONumberMandatory === 'yes',
            "poEmail": formData.invoiceStatement.poEmail,
            "hasInvoicePlatform": formData.invoiceStatement.hasInvoicePlatform === 'yes',
            "invoicePlatform": formData.invoiceStatement.invoicePlatform,
            "needCreditAccount": formData.invoiceStatement.needCreditAccount === 'yes',
            "requestedByEmail": loggedInEmail,
            "requestedDate": currentDate,
            "modifiedByEmail": loggedInEmail
        };
        // If declarationData is provided, add those fields
        let declarationItem = declarationData.fullName?.length > 0 ? declarationData : formData.declaration;
        if (declarationItem) {
            clientOnboardRequest.hasAgreedToTermsAndCondition = declarationItem.hasAgreedToTermsAndCondition;
            clientOnboardRequest.title = declarationItem.title;
            clientOnboardRequest.fullName = declarationItem.fullName;
            clientOnboardRequest.company = declarationItem.company;
            clientOnboardRequest.tandCComments = declarationItem.tandCComments || '';
            clientOnboardRequest.requestedDate = declarationItem.requestedDate;
        }

        if (id) {
            clientOnboardRequest.id = id;
            requestedContacts.forEach((contact, idx) => {
                contact.id = formData.requestedContacts[idx]?.id;
                contact[isDraftOnboarding] = id;
            });
        }

        const payload: any = {
            clientOnboardRequest,
            requestedContacts: requestedContacts
        };

        if (formData.invoiceStatement.needCreditAccount === 'yes') {
            payload.creditAccountDetail = {
                "contactName": formData.creditAccountData.contactName,
                "position": formData.creditAccountData.position,
                "email": formData.creditAccountData.email,
                "phoneNumber": formData.creditAccountData.phoneNumber,
                "companyWebsite": formData.creditAccountData.companyWebsite,
                "natureOfBusiness": formData.creditAccountData.natureOfBusiness,
                "dateOfIncorporation": formData.creditAccountData.dateOfIncorporation,
                "parentCompany": formData.creditAccountData.parentCompany,
                "previousBusiness": formData.creditAccountData.previousBusiness,
                "maxCreditRequired": normalizeMaxCreditRequired(formData.creditAccountData.maxCreditRequired),
                "isTrustee": formData.creditAccountData.isTrustee === 'yes',
                "directorName": formData.creditAccountData.directorName,
                "hasCreditReferees": formData.creditAccountData.hasCreditReferees === 'yes',
                "requestedDate": currentDate,
                "requestedByEmail": loggedInEmail,
                "modifiedByEmail": loggedInEmail
            };
            if (formData.creditAccountData?.hasCreditReferees === 'yes') {
                payload.creditReferees = formData.creditAccountData.creditReferees?.map((referee: any) => ({
                    "contactName": referee.contactName,
                    "company": referee.company,
                    "phoneNumber": referee.phoneNumber,
                    "email": referee.email
                })) || [];
            } else {
                payload.creditReferees = [];
            }
        } else {
            payload.creditAccountDetail = null;
            payload.creditReferees = [];
        }
        if (formData.invoiceStatement.needCreditAccount === 'yes' && (formData.creditAccountData?.id !== '' && formData.creditAccountData.id !== undefined && formData.creditAccountData.id !== null)) {
            payload.creditAccountDetail.id = formData?.creditAccountData?.id
        };
        if (payload?.creditAccountDetail?.id) {
            payload.creditAccountDetail[isDraftOnboarding] = id;
        }
        if (formData.invoiceStatement.needCreditAccount === 'yes' &&
            formData.creditAccountData.creditReferees &&
            formData.creditAccountData.creditReferees.length > 0 &&
            formData.creditAccountData.creditReferees.some((ref: any) => ref.id !== '' && ref.id !== null && ref.id !== undefined)
        ) {
            payload.creditReferees = formData.creditAccountData.creditReferees?.map((contact, idx) => ({
                ...contact,
                [isCreditAccountDraft]: id
            }));
        }
        if (formData.requestStatus === 5) {
            payload.clientOnboardRequest.requestStatus = 5; // Redraft status
            payload.clientOnboardRequest.reviewerComments = formData.reviewerComments || '';
            payload.clientOnboardRequest.proposeClientCode = formData.proposeClientCode;
            payload.clientOnboardRequest.proposedPriceListID = formData.proposedPriceListID;
            payload.clientOnboardRequest.proposedELECodes = formData.proposedELECodes;
            payload.clientOnboardRequest.proposedReportStateID = formData.proposedReportStateID;
            payload.clientOnboardRequest.proposedIsGSTExempt = formData.proposedIsGSTExempt;
            payload.clientOnboardRequest.proposedBUCode = formData.proposedBUCode;
            payload.clientOnboardRequest.proposedPaymentTermsID = formData.proposedPaymentTermsID;
            payload.clientOnboardRequest.proposedIsMonthlyInvoice = formData.proposedIsMonthlyInvoice;
            payload.clientOnboardRequest.proposedASM = formData.proposedASM;
            payload.clientOnboardRequest.proposedIsPORequired = formData.proposedIsPORequired;
            payload.clientOnboardRequest.abnCheck = formData.abnCheck;
            payload.clientOnboardRequest.refereeCheck = formData.refereeCheck;
            payload.clientOnboardRequest.bankLetterFileName = formData.bankLetterFileName || '';

            payload.clientOnboardRequest.id = id;

            payload.clientOnboardRequest = payload.clientOnboardRequest && Object.keys(payload.clientOnboardRequest).length > 0
                ? Object.keys(payload.clientOnboardRequest).reduce((acc, key) => {
                    if (!['requestedByEmail', 'modifiedByEmail'].includes(key)) {
                        acc[key] = payload.clientOnboardRequest[key];
                    }
                    return acc;
                }, {} as any)
                : payload.clientOnboardRequest;

            payload.creditAccountDetail = payload.creditAccountDetail && Object.keys(payload.creditAccountDetail).length > 0
                ? Object.keys(payload.creditAccountDetail).reduce((acc, key) => {
                    if (!['requestedByEmail', 'requestedDate', 'modifiedByEmail'].includes(key)) {
                        acc[key] = payload.creditAccountDetail[key];
                    }
                    return acc;
                }, {} as any)
                : payload.creditAccountDetail;

            // Remove metadata fields from requestedContacts
            payload.requestedContacts = requestedContacts.map(({ modifiedByEmail, modifiedOn, requestedByEmail, requestedOn, ...contact }) => contact);
        }
        return payload;
    };

    const handleSave = (declarationData?: any) => {
        if (!formData.companyName || formData.companyName.trim() === '') {
            showSnackbar('Company Name is required to save draft.', 'error');
            return;
        }
        setIsSaving(true);
        const payload = buildSavePayload(declarationData);
        let apiName = formData?.requestStatus === 5 ? 'UpdateRedraftRequest' : 'SaveDraft';
        let method = formData?.requestStatus === 5 ? 'put' : 'post';
        axios[method](`${process.env.REACT_APP_BASE_URL}/OnboardClientRequest/${apiName}`, payload,
            {
                responseType: 'json',
                headers: {
                    'Authorization': accessTokenValue ? `Bearer ${accessTokenValue}` : '',
                    "content-type": "application/json"
                }
            }
        ).then((response) => {
            if (response && response.status === 200) {
                showSnackbar('Saved successfully!', 'success');
                const latestRequest = response.data;
                paramMapping(latestRequest);
            } else if (response?.status === 400 && handleApiValidationError(response.data)) {
                return;
            } else {
                const errorMsg = getErrorMessage({ response }, 'Failed to save draft. Please try again.');
                showSnackbar(errorMsg, 'error');
            }
        })
            .catch((error) => {
                if (error?.response?.status === 400 && handleApiValidationError(error.response.data)) {
                    return;
                }

                const errorMessage = getErrorMessage(error, 'An unexpected error occurred. Please try again.');
                showSnackbar(errorMessage, 'error');
            })
            .finally(() => {
                setIsSaving(false);
            });
    };

    const handleSubmit = (declarationData: any) => {
        const finalFormData = {
            ...formData,
            declaration: declarationData
        };
        const resolvedSignatureFile = captureSignature instanceof File
            ? captureSignature
            : (finalFormData?.declaration?.signature instanceof File ? finalFormData.declaration.signature : null);
        const resolvedSignatureId = resolvedSignatureFile?.name
            || (formData.requestStatus === 5 ? finalFormData?.declaration?.signatureId || formData?.declaration?.signatureId || formData?.signatureId : '');

        const requestedContacts = [];

        const generalContact = {
            "name": finalFormData.contactName,
            "email": finalFormData.emailAddress,
            "emailForStatements": "",
            "phoneNumber": finalFormData.phoneNumber,
            "mobileNumber": finalFormData.mobileNumber,
            "isDefault": true,
            "isInvoice": false,
            "requestedOn": finalFormData.declaration.requestedDate,
            "requestedByEmail": loggedInEmail,
            "modifiedByEmail": loggedInEmail
        };
        requestedContacts.push(generalContact);

        if (finalFormData.invoiceStatement.contactName) {
            const invoiceContact = {
                "name": finalFormData.invoiceStatement.contactName,
                "email": finalFormData.invoiceStatement.emailAddressInvoices,
                "emailForStatements": finalFormData.invoiceStatement.emailAddressStatements,
                "phoneNumber": finalFormData.invoiceStatement.phoneNumber,
                "mobileNumber": "",
                "isDefault": false,
                "isInvoice": true,
                "requestedOn": finalFormData.declaration.requestedDate,
                "requestedByEmail": loggedInEmail,
                "modifiedByEmail": loggedInEmail
            };
            requestedContacts.push(invoiceContact);
        }

        const creditRefereesWithoutId = finalFormData.creditAccountData.creditReferees?.map(
            ({ id, creditAccountID, ...referee }: any) => referee
        ) || [];

        let obj: any = {
            clientOnboardRequest: {
                "companyName": finalFormData.companyName,
                "tradingName": finalFormData.tradingName,
                "abn": formatABN(finalFormData.abn),
                "eurofinsContactName": finalFormData.eurofinsContactName,
                "business_StreetName": finalFormData.businessAddress.street,
                "business_Suburb": finalFormData.businessAddress.suburb,
                "business_State": finalFormData.businessAddress.state,
                "business_Country": finalFormData.businessAddress.country,
                "business_PostCode": finalFormData.businessAddress.postcode,
                "isPostalAddressSameAsBusinessAddress": finalFormData.isPostalAddressSameAsBusinessAddress,
                "postal_StreetName": finalFormData.postalAddress.street,
                "postal_Suburb": finalFormData.postalAddress.suburb,
                "postal_State": finalFormData.postalAddress.state,
                "postal_Country": finalFormData.postalAddress.country,
                "postal_PostCode": finalFormData.postalAddress.postcode,
                "isPONumberMandatory": finalFormData.invoiceStatement.isPONumberMandatory === 'yes',
                "poEmail": finalFormData.invoiceStatement.poEmail,
                "hasInvoicePlatform": finalFormData.invoiceStatement.hasInvoicePlatform === 'yes',
                "invoicePlatform": finalFormData.invoiceStatement.invoicePlatform,
                "needCreditAccount": finalFormData.invoiceStatement.needCreditAccount === 'yes',
                "hasAgreedToTermsAndCondition": finalFormData.declaration.hasAgreedToTermsAndCondition,
                "tandCComments": finalFormData.declaration.tandCComments,
                "title": finalFormData.declaration.title,
                "fullName": finalFormData.declaration.fullName,
                "company": finalFormData.declaration.company,
                "requestedByEmail": loggedInEmail,
                "requestedDate": finalFormData.declaration.requestedDate,
                "modifiedByEmail": loggedInEmail,
                "signatureId": resolvedSignatureId
            },
            requestedContacts: requestedContacts
        };
        if (finalFormData.invoiceStatement.needCreditAccount === 'yes') {
            obj.creditAccountDetail = {
                "contactName": finalFormData.creditAccountData.contactName,
                "position": finalFormData.creditAccountData.position,
                "email": finalFormData.creditAccountData.email,
                "phoneNumber": finalFormData.creditAccountData.phoneNumber,
                "companyWebsite": finalFormData.creditAccountData.companyWebsite,
                "natureOfBusiness": finalFormData.creditAccountData.natureOfBusiness,
                "dateOfIncorporation": finalFormData.creditAccountData.dateOfIncorporation,
                "parentCompany": finalFormData.creditAccountData.parentCompany,
                "previousBusiness": finalFormData.creditAccountData.previousBusiness,
                "maxCreditRequired": normalizeMaxCreditRequired(finalFormData.creditAccountData.maxCreditRequired),
                "isTrustee": finalFormData.creditAccountData.isTrustee === 'yes',
                "directorName": finalFormData.creditAccountData.directorName,
                "hasCreditReferees": finalFormData.creditAccountData.hasCreditReferees === 'yes',
                "requestedDate": finalFormData.declaration.requestedDate,
                "requestedByEmail": loggedInEmail,
                "modifiedByEmail": loggedInEmail
            };
            if (finalFormData.creditAccountData.hasCreditReferees === 'yes') {
                obj.creditReferees = creditRefereesWithoutId;
            } else {
                obj.creditReferees = [];
            }
        } else {
            obj.creditAccountDetail = null;
            obj.creditReferees = [];
        }

        if (formData.requestStatus === 5) {
            if (obj?.creditAccountDetail !== null) {
                obj.creditReferees = formData.creditAccountData.creditReferees?.map((contact) => ({
                    ...contact,
                    creditAccountID: id,
                    id: contact.id
                }));
            }

            if (obj?.creditAccountDetail !== null) {
                obj.creditAccountDetail.clientOnBoardRequestID = id;
                if (formData.creditAccountData.id !== "") {
                    obj.creditAccountDetail.id = formData.creditAccountData.id;
                }
            }
            requestedContacts.forEach((contact, index) => {
                contact.id = formData.requestedContacts[index]?.id;
                contact.clientOnBoardRequestID = id;
            });
            obj.clientOnboardRequest.signatureId = resolvedSignatureId;
            obj.clientOnboardRequest.id = id;
            obj.clientOnboardRequest.requestStatus = 1;
            obj.clientOnboardRequest.proposeClientCode = formData.proposeClientCode;
            obj.clientOnboardRequest.proposedPriceListID = formData.proposedPriceListID;
            obj.clientOnboardRequest.proposedELECodes = formData.proposedELECodes;
            obj.clientOnboardRequest.proposedReportStateID = formData.proposedReportStateID;
            obj.clientOnboardRequest.proposedIsGSTExempt = formData.proposedIsGSTExempt;
            obj.clientOnboardRequest.proposedBUCode = formData.proposedBUCode;
            obj.clientOnboardRequest.proposedPaymentTermsID = formData.proposedPaymentTermsID;
            obj.clientOnboardRequest.proposedIsMonthlyInvoice = formData.proposedIsMonthlyInvoice;
            obj.clientOnboardRequest.proposedASM = formData.proposedASM;
            obj.clientOnboardRequest.proposedIsPORequired = formData.proposedIsPORequired;
            obj.clientOnboardRequest.bankLetterFileName = formData.bankLetterFileName || '';
        }

        if (id && formData.requestStatus !== 5) {
            obj.id = id;
        }

        setIsSubmitting(true);
        let apiName = formData.requestStatus === 5 ? 'UpdateRedraftRequest' : 'AddNewRequest';
        let method = formData?.requestStatus === 5 ? 'put' : 'post';
        axios[method](`${process.env.REACT_APP_BASE_URL}/OnboardClientRequest/${apiName}`, obj,
            {
                responseType: 'json',
                headers: {
                    'Authorization': accessTokenValue ? `Bearer ${accessTokenValue}` : '',
                    "content-type": "application/json"
                }
            }
        ).then((response) => {
            if (response && response.status === 200) {
                setIsSubmitting(true);
                setTimeout(() => {
                    navigate('/registration-success', { state: { email: loggedInEmail } });
                }, 3000);
                addSignatureApi(resolvedSignatureFile);
            } else if (response?.status === 400 && handleApiValidationError(response.data)) {
                return;
            } else if (response && response.status === 401) {
                const errorMsg = getErrorMessage({ response }, 'Your session has expired. Please revalidate your email to continue.');
                showSnackbar(errorMsg, 'error');
                navigate('/account-registration');
                setIsSubmitting(false);
            } else {
                const errorMsg = getErrorMessage({ response }, 'Submission failed. Please try again.');
                showSnackbar(errorMsg, 'error');
                setIsSubmitting(false);
            }
        })
            .catch((error) => {
                if (error?.response?.status === 400 && handleApiValidationError(error.response.data)) {
                    return;
                }

                const errorMessage = getErrorMessage(error, 'An unexpected error occurred. Please try again.');
                showSnackbar(errorMessage, 'error');
                setIsSubmitting(false);
            })
    };

    const getSignatureFromParent = (file: any) => {
        setCaptureSignature(file);
    }

    const addSignatureApi = (signature: any) => {
        if (signature && signature instanceof File) {
            const payload = new FormData();
            payload.append('file', signature, signature.name);
            axios.post(
                `${process.env.REACT_APP_BASE_URL}/ImageBlob/UploadClientSignature`,
                payload,
                {
                    headers: {
                        Authorization: accessTokenValue ? `Bearer ${accessTokenValue}` : ''
                    }
                }
            )
                .then(() => {
                    // Signature uploaded successfully, you can show a success message if needed
                })
                .catch((error) => {
                    setSnackbar({
                        open: true,
                        message: getErrorMessage(error, 'Unable to save signature'),
                        severity: 'error'
                    });
                });
        }
    }


    const handleGoToStep = (step: number, errors?: Record<string, string>) => {
        if (errors) {
            setValidationErrors(errors);
        } else {
            setValidationErrors({});
        }
        setActiveStep(step);
    };

    const renderStepContent = () => {
        if (skipCreditAccountStep) {
            switch (activeStep) {
                case 0:
                    return (
                        <GeneralInformation
                            formData={formData}
                            australianStates={australianStates}
                            onFormDataChange={handleFormDataChange}
                            onNext={handleNext}
                            onSkipToReview={handleSkipToReview}
                            onSave={handleSave}
                            isSaving={isSaving}
                            externalErrors={validationErrors}
                            isDisabled={isDisabled}
                        />
                    );
                case 1:
                    return (
                        <Confirmation
                            formData={formData}
                            australianStates={australianStates}
                            onBack={handleBack}
                            onFormDataChange={handleFormDataChange}
                            onBackToGeneralInfo={handleBackToGeneralInfo}
                            onSubmit={handleSubmit}
                            isSubmitting={isSubmitting}
                            onSave={handleSave}
                            isSaving={isSaving}
                            onGoToStep={handleGoToStep}
                            isDisabled={isDisabled}
                            getSignatureFromParent={getSignatureFromParent}
                        />
                    );
                default:
                    return 'Unknown step';
            }
        } else {
            switch (activeStep) {
                case 0:
                    return (
                        <GeneralInformation
                            formData={formData}
                            australianStates={australianStates}
                            onFormDataChange={handleFormDataChange}
                            onNext={handleNext}
                            onSkipToReview={handleSkipToReview}
                            onSave={handleSave}
                            isSaving={isSaving}
                            externalErrors={validationErrors}
                            isDisabled={isDisabled}
                        />
                    );
                case 1:
                    return (
                        <CreditAccountDetails
                            formData={formData}
                            onFormDataChange={handleFormDataChange}
                            onNext={handleNext}
                            onBack={handleBack}
                            onSave={handleSave}
                            isSaving={isSaving}
                            externalErrors={validationErrors}
                            isDisabled={isDisabled}
                        />
                    );
                case 2:
                    return (
                        <Confirmation
                            formData={formData}
                            australianStates={australianStates}
                            onBack={handleBack}
                            onFormDataChange={handleFormDataChange}
                            onBackToGeneralInfo={handleBackToGeneralInfo}
                            onSubmit={handleSubmit}
                            isSubmitting={isSubmitting}
                            onSave={handleSave}
                            isSaving={isSaving}
                            onGoToStep={handleGoToStep}
                            isDisabled={isDisabled}
                            getSignatureFromParent={getSignatureFromParent}
                        />
                    );
                default:
                    return 'Unknown step';
            }
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                background: 'linear-gradient(to right, #003883, #ee7d11)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                py: 4
            }}
        >
            <Container maxWidth="md">
                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 3, md: 5 },
                        borderRadius: 3,
                        background: '#ffffff',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06), 0 8px 24px rgba(0, 0, 0, 0.08)',
                        border: '1px solid #e8e8e8',
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: '5px',
                            background: 'linear-gradient(90deg, #003883 0%, #0056b3 100%)',
                        }
                    }}
                >
                    {/* Logo Section */}
                    <Box sx={{ mb: 2, textAlign: 'center' }}>
                        <Link
                            href="https://www.eurofins.com.au/environment-testing/"
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                                display: 'inline-block',
                                textDecoration: 'none',
                            }}
                        >
                            <Box
                                component="img"
                                src={ETLogo}
                                alt="Eurofins Environment Testing Logo"
                                onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                }}
                                sx={{
                                    width: {
                                        xs: '80%',   // mobile
                                        sm: '70%',   // tablet
                                        md: '450px', // desktop
                                    },
                                    maxWidth: '450px',
                                    height: 'auto',
                                    mb: 3,
                                    display: 'block',
                                    mx: 'auto',
                                }}
                            />
                        </Link>
                    </Box>

                    {/* Title Section */}
                    <Box sx={{ mb: 4, textAlign: 'center' }}>
                        <Typography
                            variant="h5"
                            component="h1"
                            sx={{
                                fontWeight: 700,
                                color: '#003883',
                                mb: 1,
                                fontSize: { xs: '18px', md: '24px' },
                                letterSpacing: '-0.02em'
                            }}
                        >
                            New Client Registration
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: '#6b7280',
                                maxWidth: 400,
                                mx: 'auto',
                                lineHeight: 1.6,
                                fontSize: '12px'
                            }}
                        >
                            Complete the form below to register as a new client
                        </Typography>
                    </Box>

                    {/* Stepper Section */}
                    <Box
                        sx={{
                            mb: 5,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: {
                                xs: 1,
                                sm: 2,
                                md: 4,
                            },
                            flexWrap: {
                                xs: 'wrap',
                                sm: 'nowrap',
                            },
                            px: 2,
                        }}
                    >
                        {steps.map((label, index) => {
                            const isActive = activeStep === index;
                            const isCompleted = activeStep > index;

                            return (
                                <Box
                                    key={label}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: {
                                                xs: 'column',
                                                sm: 'row',
                                            },
                                            alignItems: 'center',
                                            gap: {
                                                xs: 0.5,
                                                sm: 1,
                                            },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: {
                                                    xs: 26,
                                                    sm: 30,
                                                    md: 32,
                                                },
                                                height: {
                                                    xs: 26,
                                                    sm: 30,
                                                    md: 32,
                                                },
                                                borderRadius: '50%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                bgcolor:
                                                    isActive || isCompleted
                                                        ? '#003883'
                                                        : '#d0d0d0',
                                                color: '#fff',
                                                fontWeight: 600,
                                                fontSize: {
                                                    xs: '11px',
                                                    sm: '12px',
                                                },
                                                boxShadow: isActive
                                                    ? '0 0 0 3px rgba(0,56,131,.2)'
                                                    : 'none',
                                                transition: 'all .2s ease',
                                            }}
                                        >
                                            {index + 1}
                                        </Box>

                                        <Typography
                                            sx={{
                                                fontWeight: isActive ? 600 : 500,
                                                color:
                                                    isActive || isCompleted
                                                        ? '#003883'
                                                        : '#9ca3af',
                                                fontSize: {
                                                    xs: '10px',
                                                    sm: '11px',
                                                    md: '12px',
                                                },
                                                textAlign: 'center',
                                                whiteSpace: {
                                                    xs: 'normal',
                                                    sm: 'nowrap',
                                                },
                                                maxWidth: {
                                                    xs: 70,
                                                    sm: 'none',
                                                },
                                                lineHeight: 1.2,
                                            }}
                                        >
                                            {label}
                                        </Typography>
                                    </Box>

                                    {index < steps.length - 1 && (
                                        <Box
                                            sx={{
                                                width: {
                                                    sm: 20,
                                                    md: 40,
                                                },
                                                height: 2,
                                                mx: 1,
                                                bgcolor: isCompleted ? '#003883' : '#d0d0d0',
                                                display: {
                                                    xs: 'none',
                                                    sm: 'block',
                                                },
                                            }}
                                        />
                                    )}
                                </Box>
                            );
                        })}
                    </Box>

                    {formData.requestStatus === 5 &&
                        <Box
                            sx={{
                                mt: 1,
                                p: 1,
                                mb: 3,
                                backgroundColor: '#fef3c7',
                                border: '2px solid #f59e0b',
                                borderRadius: 2,
                                display: 'flex',
                                gap: 2,
                                alignItems: 'flex-start'
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minWidth: 32,
                                    height: 32,
                                    borderRadius: '50%',
                                    backgroundColor: '#f59e0b',
                                    color: '#fff',
                                    fontWeight: 700,
                                    fontSize: '16px'
                                }}
                            >
                                !
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Typography
                                    sx={{
                                        fontWeight: 700,
                                        color: '#92400e',
                                        mb: 0.5,
                                        fontSize: '14px'
                                    }}
                                >
                                    Action Required
                                </Typography>
                                <Box
                                    sx={{
                                        color: '#78350f',
                                        fontSize: '13px',
                                        lineHeight: 1.6,
                                    }}
                                >
                                    <ul style={{ margin: '8px 0 0 20px', padding: 0 }}>
                                        <li style={{ marginBottom: '6px' }}>{formData.reviewerComments}</li>
                                    </ul>
                                </Box>
                            </Box>
                        </Box>
                    }

                    {isDisabled &&
                        <Box
                            sx={{
                                mt: 1,
                                p: 1,
                                mb: 3,
                                backgroundColor: '#fef3c7',
                                border: '2px solid #f59e0b',
                                borderRadius: 2,
                                display: 'flex',
                                gap: 2,
                                alignItems: 'flex-start'
                            }}
                        >
                            <Typography
                                sx={{
                                    fontWeight: 700,
                                    color: '#f00000',
                                    mb: 0.5,
                                    fontSize: '14px'
                                }}>
                                An existing request is already in progress. Please wait for it to complete before submitting a new one.
                            </Typography>
                        </Box>
                    }

                    {/* Form Content */}
                    <Box
                        sx={{
                            px: { xs: 0, md: 2 }
                        }}
                    >
                        {renderStepContent()}
                    </Box>
                </Paper>

                {/* Footer */}
                <Box
                    sx={{
                        mt: 4,
                        textAlign: 'center'
                    }}
                >
                    <Typography
                        variant="body2"
                        sx={{ color: '#ffffff', fontSize: '12px', fontWeight: 600 }}
                    >
                        © {new Date().getFullYear()} Eurofins Environment Testing. All rights reserved.
                    </Typography>
                </Box>
            </Container>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{
                        width: '100%',
                        borderRadius: 2,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                    }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};
